/**
 * Edit eWarranty Modal (admin-only).
 *
 * Fetches a warranty by id, lets the admin tweak any field — customer,
 * vehicle, service details, status, verification notes — and PUTs the
 * delta back to /api/warranty/registrations/{id}. Backend handles partial
 * updates via the expanded `EWarrantyUpdate` model.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Save, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from './shared';

const SERVICE_TYPES = [
  'Paint Protection Film',
  'Ceramic Coating',
  'Sunfilm / Window Tint',
  'Graphene Coating',
  'Glass Coating',
  'Headlight Protection',
  'Anti-Rust Underbody',
  'Detailing',
  'Other',
];

const STATUS_OPTIONS = [
  { value: 'under_review', label: 'Under review' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'approved', label: 'Approved · Active' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
];

const isoDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
};

const EditWarrantyModal = ({ warrantyId, onClose, onSaved }) => {
  const { authHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const { data } = await axios.get(
          `${API}/warranty/registrations/${warrantyId}`,
          { headers: authHeader() },
        );
        if (!alive) return;
        setForm({
          status: data.status || 'under_review',
          verification_notes: data.verification_notes || '',
          warranty_expiry: isoDate(data.warranty_expiry),
          lifetime_warranty: !!data.lifetime_warranty,
          customer_name: data.customer_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          pincode: data.pincode || '',
          vehicle_make: data.vehicle_make || '',
          vehicle_model: data.vehicle_model || '',
          vehicle_year: data.vehicle_year || new Date().getFullYear(),
          vehicle_color: data.vehicle_color || '',
          vehicle_registration: data.vehicle_registration || '',
          service_type: data.service_type || 'Paint Protection Film',
          installation_date: isoDate(data.installation_date),
          installer_name: data.installer_name || '',
          installer_location: data.installer_location || '',
          invoice_number: data.invoice_number || '',
          product_used: data.product_used || '',
          coverage_area: data.coverage_area || '',
          roll_id: data.roll_id || '',
        });
      } catch (e) {
        if (alive) setErr(formatErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [warrantyId, authHeader]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form) return;
    setSaving(true);
    setErr('');
    try {
      // Strip empty strings → omit so backend doesn't overwrite required
      // fields with blanks. Numeric and required fields stay as-is.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        // Booleans must survive even when false, otherwise turning Lifetime
        // back off would never reach the server.
        if (typeof v === 'boolean') { payload[k] = v; return; }
        if (v === '' || v === null || v === undefined) return;
        if (k === 'vehicle_year') {
          payload[k] = Number(v) || undefined;
        } else {
          payload[k] = v;
        }
      });
      if (!payload.vehicle_registration) {
        setErr('Registration number is required.');
        setSaving(false);
        return;
      }
      const { data } = await axios.put(
        `${API}/warranty/registrations/${warrantyId}`,
        payload,
        { headers: authHeader() },
      );
      onSaved && onSaved(data);
      onClose && onClose();
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-warranty-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-teal-600" />
            <div>
              <h2 className="text-base font-semibold text-slate-900">Edit eWarranty</h2>
              <p className="text-xs font-mono text-slate-500 mt-0.5">{warrantyId}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="edit-warranty-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="py-16 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading warranty…</div>
          ) : !form ? (
            <div className="py-12 text-center text-red-700">{err || 'Failed to load.'}</div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {/* Status & Expiry */}
              <Section title="Status & Verification">
                <Grid>
                  <Field label="Status">
                    <select value={form.status} onChange={(e) => set('status', e.target.value)} className={inputCls} data-testid="edit-status">
                      {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Warranty Expiry">
                    <input
                      type="date"
                      value={form.lifetime_warranty ? '' : form.warranty_expiry}
                      disabled={form.lifetime_warranty}
                      onChange={(e) => set('warranty_expiry', e.target.value)}
                      className={`${inputCls} disabled:bg-slate-100 disabled:text-slate-400`}
                      data-testid="edit-expiry"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
                      <input
                        type="checkbox"
                        checked={form.lifetime_warranty}
                        onChange={(e) => set('lifetime_warranty', e.target.checked)}
                        data-testid="edit-lifetime"
                      />
                      Lifetime warranty (certificate shows “Lifetime”)
                    </label>
                  </Field>
                </Grid>
                <Field label="Verification Notes (visible on status email)">
                  <textarea rows={2} value={form.verification_notes} onChange={(e) => set('verification_notes', e.target.value)} className={`${inputCls} resize-y`} data-testid="edit-notes" />
                </Field>
              </Section>

              {/* Customer */}
              <Section title="Customer">
                <Grid>
                  <Field label="Name">
                    <input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} className={inputCls} data-testid="edit-name" />
                  </Field>
                  <Field label="Phone">
                    <input value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} data-testid="edit-phone" />
                  </Field>
                  <Field label="Email">
                    <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid="edit-email" />
                  </Field>
                  <Field label="City">
                    <input value={form.city} onChange={(e) => set('city', e.target.value)} className={inputCls} data-testid="edit-city" />
                  </Field>
                  <Field label="State" className="sm:col-span-1">
                    <input value={form.state} onChange={(e) => set('state', e.target.value)} className={inputCls} data-testid="edit-state" />
                  </Field>
                  <Field label="Pincode">
                    <input value={form.pincode} onChange={(e) => set('pincode', e.target.value)} className={inputCls} data-testid="edit-pincode" />
                  </Field>
                </Grid>
                <Field label="Address">
                  <textarea rows={2} value={form.address} onChange={(e) => set('address', e.target.value)} className={`${inputCls} resize-y`} data-testid="edit-address" />
                </Field>
              </Section>

              {/* Vehicle */}
              <Section title="Vehicle">
                <Grid>
                  <Field label="Make">
                    <input value={form.vehicle_make} onChange={(e) => set('vehicle_make', e.target.value)} className={inputCls} data-testid="edit-make" />
                  </Field>
                  <Field label="Model">
                    <input value={form.vehicle_model} onChange={(e) => set('vehicle_model', e.target.value)} className={inputCls} data-testid="edit-model" />
                  </Field>
                  <Field label="Year">
                    <input type="number" value={form.vehicle_year} onChange={(e) => set('vehicle_year', e.target.value)} className={inputCls} data-testid="edit-year" />
                  </Field>
                  <Field label="Color">
                    <input value={form.vehicle_color} onChange={(e) => set('vehicle_color', e.target.value)} className={inputCls} data-testid="edit-color" />
                  </Field>
                  <Field label="Registration Number *">
                    <input value={form.vehicle_registration} onChange={(e) => set('vehicle_registration', e.target.value.toUpperCase())} className={`${inputCls} font-mono tracking-wider`} data-testid="edit-reg" required />
                  </Field>
                </Grid>
              </Section>

              {/* Service */}
              <Section title="Service Details">
                <Grid>
                  <Field label="Service Type">
                    <select value={form.service_type} onChange={(e) => set('service_type', e.target.value)} className={inputCls} data-testid="edit-service-type">
                      {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Installation Date">
                    <input type="date" value={form.installation_date} onChange={(e) => set('installation_date', e.target.value)} className={inputCls} data-testid="edit-install-date" />
                  </Field>
                  <Field label="Installer">
                    <input value={form.installer_name} onChange={(e) => set('installer_name', e.target.value)} className={inputCls} data-testid="edit-installer" />
                  </Field>
                  <Field label="Location">
                    <input value={form.installer_location} onChange={(e) => set('installer_location', e.target.value)} className={inputCls} data-testid="edit-location" />
                  </Field>
                  <Field label="Product Used">
                    <input value={form.product_used} onChange={(e) => set('product_used', e.target.value)} placeholder="e.g. UniGuard Pro Gloss" className={inputCls} data-testid="edit-product" />
                  </Field>
                  <Field label="Coverage Area">
                    <input value={form.coverage_area} onChange={(e) => set('coverage_area', e.target.value)} placeholder="Full body / Partial / Hood + Fenders" className={inputCls} data-testid="edit-coverage" />
                  </Field>
                  <Field label="Roll ID (Sunfilm only)">
                    <input value={form.roll_id} onChange={(e) => set('roll_id', e.target.value)} className={inputCls} data-testid="edit-roll" />
                  </Field>
                  <Field label="Linked Invoice #">
                    <input value={form.invoice_number} onChange={(e) => set('invoice_number', e.target.value)} className={inputCls} data-testid="edit-invoice-num" />
                  </Field>
                </Grid>
              </Section>

              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="edit-err">{err}</div>}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 -mx-5 px-5 pb-1 sticky bottom-0 bg-white">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-lg"
                  data-testid="edit-warranty-save"
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-2.5">
    <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-100 pb-1">{title}</h3>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
);

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

export default EditWarrantyModal;
